import type { AdSlotId, NewsUkAdSlotConfig } from './types';

const PREBID_LIBRARY_SRC = 'https://ads.dreamteamfc.com/prebid.dreamteamfc.min.js';
const AD_LIBRARY_SRC = 'https://ads.dreamteamfc.com/ads.dreamteamfc.min.js';
const AD_LIBRARY_SCRIPT_SOURCES = [PREBID_LIBRARY_SRC, AD_LIBRARY_SRC] as const;

const SLOT_RENDER_RETRY_INTERVAL_MS = 250;
const SLOT_RENDER_MAX_WAIT_MS = 10000;

let loadAdLibraryPromise: Promise<void> | null = null;

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const ensureAdCommandQueue = () => {
  window.newsUkAdLibrary = window.newsUkAdLibrary ?? {};
  window.newsUkAdLibrary.cmd = window.newsUkAdLibrary.cmd ?? [];

  return window.newsUkAdLibrary.cmd;
};

const findScriptBySrc = (src: string) => {
  return Array.from(document.scripts).find(script => script.src === src);
};

const loadScript = (src: string) => {
  if (findScriptBySrc(src)) return Promise.resolve();

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');

    script.type = 'text/javascript';
    script.src = src;
    script.async = false;
    script.dataset.newsUkAdLibrary = 'true';

    script.addEventListener(
      'load',
      () => {
        script.dataset.newsUkAdLibraryLoaded = 'true';
        resolve();
      },
      { once: true }
    );

    script.addEventListener(
      'error',
      () => {
        script.remove();
        reject(new Error(`Unable to load News UK ad script: ${src}`));
      },
      { once: true }
    );

    document.body.appendChild(script);
  });
};

export const loadAdLibrary = () => {
  if (!isBrowser()) return Promise.resolve();

  ensureAdCommandQueue();

  loadAdLibraryPromise =
    loadAdLibraryPromise ??
    AD_LIBRARY_SCRIPT_SOURCES.reduce<Promise<void>>(
      (promise, src) => promise.then(() => loadScript(src)),
      Promise.resolve()
    ).catch(error => {
      loadAdLibraryPromise = null;
      throw error;
    });

  return loadAdLibraryPromise;
};

const hasGptSlot = (id: AdSlotId) => {
  try {
    return (
      window.googletag
        ?.pubads()
        .getSlots()
        .some(slot => slot.getSlotElementId() === id) ?? false
    );
  } catch {
    return false;
  }
};

const hasSlotAlreadyRendered = (id: AdSlotId) => {
  return window.newsUkAdLibraryConfig?.config.slotsRendered?.includes(id) ?? false;
};

const getSlotConfig = (id: AdSlotId) => {
  return window.newsUkAdLibrary?.getSlotConfigsById?.(id)?.[id];
};

const renderMissedSlot = (id: AdSlotId, waitedMs = 0) => {
  const libraryConfig = window.newsUkAdLibraryConfig?.config;

  if (!libraryConfig?.isCompleted) {
    if (waitedMs >= SLOT_RENDER_MAX_WAIT_MS) return;

    window.setTimeout(
      () => renderMissedSlot(id, waitedMs + SLOT_RENDER_RETRY_INTERVAL_MS),
      SLOT_RENDER_RETRY_INTERVAL_MS
    );
    return;
  }

  if (!document.getElementById(id) || hasSlotAlreadyRendered(id) || hasGptSlot(id)) return;

  const slotConfig = getSlotConfig(id);

  if (!slotConfig) return;

  void window.newsUkAdLibrary?.dynamicSlotRender?.({
    [id]: slotConfig as NewsUkAdSlotConfig,
  });
};

export const queueAdSlotRender = (id: AdSlotId) => {
  if (!isBrowser()) return;

  ensureAdCommandQueue().push(() => renderMissedSlot(id));
};
